import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  TrendingUp,
  UserPlus,
  Wallet,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { KPICard, ExportButton, SearchInput } from "@/components/admin/shared";
import { customersApi } from "@/lib/admin-api/crm";
import type {
  CustomerListSegment,
} from "@/lib/admin-api/crm";
import type { CustomerSummary } from "./ClientsCRM/types";
import {
  SEGMENT_LABELS,
  PAGE_SIZE,
  ALL_SEGMENTS,
  REGION_OPTIONS,
  EMPTY_STATS,
} from "./ClientsCRM/constants";
import { toCustomerSummary } from "./ClientsCRM/toCustomerSummary";
import { CustomerDetail } from "./ClientsCRM/CustomerDetail";
import { CustomersTable } from "./ClientsCRM/CustomersTable";

// ---------------------------------------------------------------------------
// Main CRM page
// ---------------------------------------------------------------------------

export default function ClientsCRM() {
  useAdminPageTitle("Clients");
  const [search, setSearch] = useState("");
  const [activeSegment, setActiveSegment] = useState<CustomerListSegment | "all">("all");
  const [activeRegion, setActiveRegion] = useState("Toutes");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // ---------------------------------------------------------------------------
  // List query — segment and region filters are now server-side, so changing
  // them triggers a refetch against the filtered dataset instead of hiding
  // rows on the current page.
  // ---------------------------------------------------------------------------

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "admin",
      "customers",
      "list",
      page,
      search,
      activeSegment,
      activeRegion,
    ],
    queryFn: () =>
      customersApi.list({
        page,
        limit: PAGE_SIZE,
        search: search || null,
        // Translate sentinel UI values into "no filter" for the wire format
        // so the server doesn't have to special-case them and network traffic
        // is minimized.
        segment: activeSegment === "all" ? null : activeSegment,
        region: activeRegion === "Toutes" ? null : activeRegion,
      }),
    placeholderData: (prev) => prev,
  });

  // ---------------------------------------------------------------------------
  // Stats query — independent of pagination and filters. Always reflects the
  // full dataset so KPI cards and segment tab counts stay stable as the user
  // paginates or narrows the list.
  // ---------------------------------------------------------------------------

  const { data: statsData } = useQuery({
    queryKey: ["admin", "customers", "stats"],
    queryFn: () => customersApi.stats(),
    placeholderData: (prev) => prev,
  });

  const stats = statsData ?? EMPTY_STATS;

  const apiCustomers: readonly CustomerSummary[] = (data?.customers ?? []).map(
    toCustomerSummary,
  );

  const totalFromApi = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalFromApi / PAGE_SIZE));

  const handleSegmentChange = useCallback((seg: CustomerListSegment | "all") => {
    setActiveSegment(seg);
    setPage(1);
  }, []);

  const handleRegionChange = useCallback((region: string) => {
    setActiveRegion(region);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const exportColumns = [
    { key: "full_name", label: "Nom" },
    { key: "phone", label: "Téléphone" },
    { key: "email", label: "Email" },
    { key: "region", label: "Région" },
    { key: "segment", label: "Segment" },
    { key: "total_orders", label: "Commandes" },
    { key: "total_spent_fcfa", label: "Total dépensé (FCFA)" },
    { key: "last_order_date", label: "Dernière commande" },
  ];

  const selectedCustomer = apiCustomers.find((c) => c.id === selectedCustomerId) ?? null;

  // ---------------------------------------------------------------------------
  // Detail view
  // ---------------------------------------------------------------------------
  if (selectedCustomer) {
    return (
      <AdminLayout>
        <CustomerDetail
          customer={selectedCustomer}
          onBack={() => setSelectedCustomerId(null)}
        />
      </AdminLayout>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------
  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              Clients
            </h1>
            <p className="text-sm text-stone-500">
              Gestion de la relation client et segmentation
            </p>
          </div>
          <ExportButton
            data={apiCustomers as unknown as Record<string, unknown>[]}
            columns={exportColumns}
            filename="clients-djayma"
          />
        </div>

        {/* KPI cards — all values come from the stats endpoint, never from
            the current page. No hardcoded trend props: we only render real
            period-over-period deltas, and that data doesn't exist yet. */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Total clients"
            value={stats.total}
            icon={<Users className="h-4 w-4" />}
            format="number"
          />
          <KPICard
            label="Nouveaux ce mois"
            value={stats.new_this_month}
            icon={<UserPlus className="h-4 w-4" />}
            format="number"
          />
          <KPICard
            label="Valeur moyenne"
            value={stats.avg_value_fcfa}
            icon={<Wallet className="h-4 w-4" />}
            format="currency"
          />
          <KPICard
            label="Taux de rétention"
            value={stats.retention_rate}
            icon={<TrendingUp className="h-4 w-4" />}
            format="percent"
          />
        </div>

        {/* Search + filters */}
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="w-full max-w-sm">
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder="Rechercher par nom, téléphone, email..."
              />
            </div>
            <select
              value={activeRegion}
              onChange={(e) => handleRegionChange(e.target.value)}
              className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {REGION_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r === "Toutes" ? "Toutes les régions" : r}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            {ALL_SEGMENTS.map((seg) => (
              <button
                key={seg}
                onClick={() => handleSegmentChange(seg)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeSegment === seg
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {seg === "all" ? "Tous" : SEGMENT_LABELS[seg]}
                {seg !== "all" && (
                  <span className="ml-1.5 text-stone-400">
                    ({stats.segment_counts[seg]})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Customer table */}
        <CustomersTable
          customers={apiCustomers}
          isLoading={isLoading}
          isError={isError}
          hasData={Boolean(data)}
          totalFromApi={totalFromApi}
          totalPages={totalPages}
          page={page}
          onPageChange={setPage}
          onSelectCustomer={setSelectedCustomerId}
        />
      </div>
    </AdminLayout>
  );
}
